import GroupRate from "features/newMarketplace/common/GroupRate";
import styled, { useTheme } from "styled-components";
import { Users } from "lucide-react";
import NewLookScoreBadge from "features/newMarketplace/NewLook/NewLookScoreBadge";
import {
  GroupLogo,
  GroupRatingInline,
} from "features/newMarketplace/NewLook/NewLookGroupParts";
import Title from "common/Typography/Title";
import LinkOutlined from "@ant-design/icons/LinkOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import UserOutlined from "@ant-design/icons/UserOutlined";
import Button, { buttonStyles } from "common/Buttons/Button";
import {
  MARKETPLACE_PAYMENTS_FEATURE_FLAG,
  MOBILE_SEARCH,
} from "common/constants";
import React, { useContext } from "react";
import ConnectExternalAccountModal from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectExternalAccountModal";
import {
  GroupData,
  SubGroupOverview,
  TagName,
  LanguageNameSpaces,
} from "@shared/types";
import { typography } from "@shared/common/Themes/typography";
import Text from "common/Typography/Text";
import MarketplaceTag from "features/newMarketplace/common/MarketplaceTag";
import useSubGroupInfo from "@shared/features/newMarketplace/hooks/useSubGroupInfo";
import GroupOverviewProvider from "@shared/features/onboarding/GroupOverviewProvider";
import { displayResultMessage, openMessage } from "utils/misc";
import useMedia from "utils/hooks/useMedia";
import { buildMarketPlaceGroupEvent } from "utils/analytics";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { useGroupSubscriptionData } from "features/settings/common/hooks";
import useConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/useConnectTelegram";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";
import Flex from "common/Boxes/Flex";
import ScoreTag from "features/newMarketplace/common/ScoreTag";
import GroupPrice from "features/newMarketplace/GroupExtraDetails/Info/GroupPrice";
import ConnectGroupModal from "features/newMarketplace/GroupExtraDetails/JoinGroup/ConnectGroupModal";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import { NEW_LOOK_DRAWER_CLOSE_HIT } from "common/Drawers/Drawer";

interface SubGroupInfoProps {
  subGroupOverview: SubGroupOverview;
  groupPagePosition?: number;
  userGroupData: GroupData;
  setIsScoreHover?: (isScoreHover?: boolean) => void;
  onClickScore?: () => void;
}

const GroupInfo: React.FC<SubGroupInfoProps> = ({
  subGroupOverview,
  groupPagePosition,
  userGroupData,
  setIsScoreHover,
  onClickScore,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { analytics } = useContext(AnalyticsContext);
  const { isNewLook } = useTheme();

  const { actionButton, freeTrialModal, expirationText } =
    useGroupSubscriptionData(userGroupData);

  const sendAnalyticsEvent = () => {
    if (groupPagePosition)
      analytics?.marketplaceGroupJoinGroupClicked(
        buildMarketPlaceGroupEvent(subGroupOverview, groupPagePosition)
      );
  };

  const {
    user,
    externalAccountType,
    externalAccountModalVisible,
    connectGroupModalVisible,
    setConnectGroupModalVisible,
    groupLink,
    buttonData,
    onCancel,
    onJoinGroupClick,
    setExternalAccountType,
    setExternalAccountModalVisible,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    isHighRisk,
  } = useSubGroupInfo(
    subGroupOverview,
    userGroupData,
    displayResultMessage,
    openMessage,
    sendAnalyticsEvent
  );

  const { queryCode, queryUuid } = useConnectTelegram(
    externalAccountType,
    setExternalAccountType,
    setExternalAccountModalVisible
  );

  const joinButton = (
    <StyledButton
      type="primary"
      onClick={buttonData?.onClick}
      icon={buttonData?.addIcon ? <PlusOutlined /> : undefined}
      style={{ fontWeight: typography.fontWeights.SEMI_BOLD }}
      $isMobile={isMobile}
      $disabled={buttonData?.disabled}
    >
      {buttonData?.buttonText}
    </StyledButton>
  );

  const joinGroupRow = (
    <Flex>
      <GroupPrice subGroupOverview={subGroupOverview} />
      {joinButton}
    </Flex>
  );

  const hasPayments =
    userGroupData.support_payments &&
    user?.hasFeatureFlag(MARKETPLACE_PAYMENTS_FEATURE_FLAG);

  const paymentsButton = (
    <StyledButton
      onClick={actionButton?.onClick || onJoinGroupClick}
      type="primary"
      href={groupLink}
      target="_blank"
      icon={actionButton ? undefined : <LinkOutlined />}
    >
      {actionButton?.text || t("group_info.contact_externally")}
    </StyledButton>
  );

  const paymentsInfo = expirationText ? (
    <Text type="secondary">{expirationText}</Text>
  ) : (
    <GroupPrice subGroupOverview={subGroupOverview} />
  );

  const getActionRow = () => {
    if (hasPayments) {
      return (
        <>
          {paymentsButton}
          {paymentsInfo}
        </>
      );
    }
    return joinGroupRow;
  };

  // New look: price on the left, the call to action on the right.
  const newLookActionRow = hasPayments ? (
    <>
      {paymentsInfo}
      {paymentsButton}
    </>
  ) : (
    <>
      <GroupPrice subGroupOverview={subGroupOverview} />
      {joinButton}
    </>
  );

  const logoWithScore = (
    <div style={{ position: "relative" }}>
      <Logo src={subGroupOverview.image_path} $isMobile={isMobile} />
      <ScoreTag
        score={subGroupOverview.backtesting_score}
        setIsScoreHover={setIsScoreHover}
        onClick={onClickScore}
        style={{
          maxWidth: 46,
          position: "absolute",
          bottom: -12,
          right: 4,
        }}
      />
    </div>
  );

  const nameAndRate = (
    <div>
      <Title level={2}>{subGroupOverview.name}</Title>
      <Flex $justifyContent="flex-start">
        <GroupRate
          groupAvgStars={subGroupOverview.avg_stars || 0}
          groupNumReviewers={subGroupOverview.num_of_reviewers}
          starsSize={14}
          isShort={true}
        />
        <TagWrapper>
          <MarketplaceTag
            tagName={TagName.TOP_SELLER}
            group={subGroupOverview}
          />
        </TagWrapper>
      </Flex>
      <Flex $justifyContent="flex-start">
        <StyledUserOutlined />
        <Text type="secondary">
          {t("group_info.subscribers", {
            count: subGroupOverview.number_of_subscribers,
          })}
        </Text>
      </Flex>
    </div>
  );

  const modals = (
    <>
      <ConnectExternalAccountModal
        visible={externalAccountModalVisible}
        externalAccountType={externalAccountType}
        onCancel={onCancel}
        code={queryCode}
        uuid={queryUuid}
      />
      {freeTrialModal}
      <GroupOverviewProvider>
        {buttonData && (
          <ConnectGroupModal
            visible={connectGroupModalVisible}
            closeModal={() => setConnectGroupModalVisible(false)}
            groupData={userGroupData}
            modalData={buttonData}
            isHighRisk={isHighRisk}
          />
        )}
      </GroupOverviewProvider>
      <NoExchangeConnectedModal
        placement="marketplace_group"
        open={noExchangeModalVisible}
        onClose={() => setNoExchangeModalVisible(false)}
        action="continue"
      />
    </>
  );

  if (isNewLook) {
    return (
      <>
        <NewLookSummary>
          <NewLookHeader>
            <GroupLogo
              image={subGroupOverview.image_path}
              name={subGroupOverview.name}
              size={isMobile ? 44 : 48}
            />
            <NewLookIdentity>
              <NewLookTitleRow>
                <NewLookName>{subGroupOverview.name}</NewLookName>
                <NewLookScoreBadge
                  score={subGroupOverview.backtesting_score}
                  setIsScoreHover={setIsScoreHover}
                  onClick={onClickScore}
                />
              </NewLookTitleRow>
              <NewLookMeta>
                <GroupRatingInline
                  avgStars={subGroupOverview.avg_stars}
                  numReviewers={subGroupOverview.num_of_reviewers}
                />
                <NewLookMetaItem>
                  <Users size={13} strokeWidth={1.75} aria-hidden={true} />
                  {t("group_info.subscribers", {
                    count: subGroupOverview.number_of_subscribers,
                  })}
                </NewLookMetaItem>
                <MarketplaceTag
                  tagName={TagName.TOP_SELLER}
                  group={subGroupOverview}
                />
              </NewLookMeta>
            </NewLookIdentity>
          </NewLookHeader>
          <NewLookActionPanel>{newLookActionRow}</NewLookActionPanel>
        </NewLookSummary>
        {modals}
      </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Flex $justifyContent="flex-start">
          {logoWithScore}
          {nameAndRate}
        </Flex>
        <ButtonPriceWrapper>{getActionRow()}</ButtonPriceWrapper>
      </div>
      {modals}
    </>
  );
};

export default GroupInfo;

const Logo = styled.img<{ $isMobile: boolean }>`
  border-radius: 6px;
  margin-right: 20px;
  width: 64px;
  height: 64px;
`;
const TagWrapper = styled.div`
  margin-left: 10px;
  margin-top: 2px;
`;
const ButtonPriceWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  justify-content: flex-end;
`;
const StyledButton = styled(Button)<{
  $isMobile?: boolean;
  $disabled?: boolean;
}>`
  ${(p) =>
    p.$isMobile &&
    `
    padding: 4px 8px;
    font-size: ${p.theme.typography.fontSizes.S};
  `}
  ${(p) =>
    p.$disabled &&
    `
    ${buttonStyles.antBtnDisabled}
  `}
`;
/** The drawer's lead: identity and the call to action. */
const NewLookSummary = styled.div`
  margin-bottom: 8px;
  /* Desktop: the drawer's close button gets its own strip above the card. */
  margin-top: 24px;
  padding: 20px 24px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  /* Phone: the sheet is the surface; no card inside it. */
  @media (max-width: ${MOBILE_SEARCH}) {
    margin-top: 0;
    margin-bottom: 0;
    padding: 0 0 4px;
    background: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
`;
const NewLookHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding-right: 28px;
  text-align: left;
  /* Phone: the drawer's close button keeps its own spot on this row. */
  @media (max-width: ${MOBILE_SEARCH}) {
    gap: 12px;
    min-height: ${NEW_LOOK_DRAWER_CLOSE_HIT}px;
    padding-right: ${NEW_LOOK_DRAWER_CLOSE_HIT - 8}px;
  }
`;
const NewLookIdentity = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const NewLookTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
const NewLookName = styled.h2`
  margin: 0;
  min-width: 0;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 18px;
  line-height: 24px;
  font-weight: 520;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: ${MOBILE_SEARCH}) {
    ${newLookPhoneType.headerTitle};
  }
`;
const NewLookMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
`;
const NewLookMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const NewLookActionPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0 0;
  padding: 16px 0 0;
  @media (max-width: ${MOBILE_SEARCH}) {
    margin-top: 12px;
    padding-top: 12px;
  }
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  text-align: left;
`;
const StyledUserOutlined = styled(UserOutlined)`
  margin-right: 6px;
  color: ${(p) => p.theme.colors.secondaryGrey};
`;
